import { concerns } from "@/mocks/home";
import { SHARED_PATIENT_IMAGE } from "@/mocks/sharedPatient";

type Props = {
  activeIndex: number;
  onSelect: (i: number) => void;
  onOpenConsult: () => void;
  onOpenDetail?: (id: string) => void;
};

export default function SectionConcern({ activeIndex, onSelect, onOpenConsult, onOpenDetail }: Props) {
  const active = concerns[activeIndex];

  return (
    <section id="concern" className="relative py-space-ds-section-standard md:py-space-ds-section-standard-md bg-white">
      <div className="w-full px-6 md:px-10">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <div className="text-ds-eyebrow text-ink-muted mb-space-ds-intro-eyebrow-heading">
              02 · Concern Finder
            </div>
            <h2 className="text-ds-feature font-medium text-ink max-w-[560px]">
              What would you like to improve?
            </h2>
            <p className="mt-5 text-ds-body text-ink-muted max-w-[520px]">
              Choose what's most on your mind. We'll show you a real patient with a similar starting point.
            </p>

            <ul className="mt-10 md:mt-14">
              {concerns.map((c, i) => {
                const isActive = i === activeIndex;
                return (
                  <li key={c.id}>
                    <button
                      onClick={() => {
                        if (c.id === "06") {
                          onOpenConsult();
                          return;
                        }
                        onSelect(i);
                        onOpenDetail?.(c.id);
                      }}
                      className="w-full text-left group flex items-center gap-6 py-5 md:py-6 border-t border-gray-200 last:border-b transition cursor-pointer"
                    >
                      <span
                        className={`w-10 md:w-14 text-[13px] md:text-[15px] font-medium transition ${
                          isActive ? "text-brand" : "text-ink-faint"
                        }`}
                      >
                        {c.id}
                      </span>
                      <span
                        className={`flex-1 text-ds-list-title transition ${
                          isActive ? "text-ink" : "text-ink group-hover:text-ink"
                        }`}
                      >
                        {c.label}
                      </span>
                      <span
                        className={`w-10 h-10 flex items-center justify-center rounded-full transition ${
                          isActive ? "text-brand" : "text-ink-faint group-hover:text-ink"
                        }`}
                      >
                        <i className={`${isActive ? "ri-arrow-right-line" : "ri-add-line"} text-lg`} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-28">
            {/* Slot for the shared patient image */}
            <div
              data-slot="concern"
              className="w-full aspect-[4/5] rounded-[14px] overflow-hidden"
              style={{ background: "transparent", boxShadow: "none" }}
            >
              <img
                src={SHARED_PATIENT_IMAGE}
                alt="Nordin Dental patient in consultation room"
                className="w-full h-full object-cover block"
                style={{ objectPosition: "65% 50%", opacity: 0 }}
              />
            </div>
            <div className="mt-5 flex items-start justify-between gap-4">
              <div>
                <div className="text-ds-meta font-medium text-ink">
                  {active.name}{active.age ? `, ${active.age}` : ""}
                </div>
                <div className="text-ds-caption text-ink-muted mt-1">Concern · {active.concern}</div>
                <div className="text-ds-caption text-ink-muted">Direction · {active.direction}</div>
              </div>
              <a
                href="#results"
                className="text-ds-caption text-brand hover:underline flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                View Story <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}